import {
  Modal, Box, Typography, Button, IconButton, CircularProgress,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { BiX, BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import API_URLS from "../../../constants/apiConstants";

// ── Constants ─────────────────────────────────────────────────────────────────
const TIMES = [
  "9:00 AM",  "9:30 AM",  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM",  "1:30 PM",  "2:00 PM",  "2:30 PM",
  "3:00 PM",  "3:30 PM",  "4:00 PM",  "4:30 PM",
  "5:00 PM",  "5:30 PM",  "6:00 PM",  "6:30 PM",
  "7:00 PM",  "7:30 PM",  "8:00 PM",  "8:30 PM",
  "9:00 PM",  "9:30 PM",  "10:00 PM", "10:30 PM",
  "11:00 PM", "11:30 PM", "12:00 AM", "12:30 AM",
];
const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const INITIAL_FORM = {
  email: "", fullName: "", center_name: "",
  size: "", country: "", type: "", info: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getMinDate() {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildCalendarCells(year, month) {
  const firstDay  = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstDay).fill(null).concat(
    Array.from({ length: totalDays }, (_, i) => i + 1)
  );
  while (cells.length % 7) cells.push(null);
  return cells;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BookDemoModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const [step, setStep]           = useState(1);
  const [form, setForm]           = useState(INITIAL_FORM);
  const [errors, setErrors]       = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [centerTypes, setCenterTypes] = useState([]);
  const [countries, setCountries]     = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [calDate, setCalDate] = useState(() => {
    const d = getMinDate();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selDay, setSelDay]   = useState(null);
  const [selTime, setSelTime] = useState(null);

  const [bookedSlots, setBookedSlots]   = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // ── Reset ──────────────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setStep(1); setForm(INITIAL_FORM); setErrors({});
    setSelDay(null); setSelTime(null);
    setBookedSlots([]); setLoadingSlots(false); setSubmitted(false);
  }, []);

  const handleClose = () => { resetForm(); onClose(); };

  useEffect(() => { if (!open) resetForm(); }, [open, resetForm]);

  // ── Fetch Options ──────────────────────────────────────────────────────────
  const fetchOptions = useCallback(async () => {
    setLoadingData(true);
    try {
      const [programsRes, countriesRes] = await Promise.all([
        axios.get(API_URLS.programType),
        axios.get(API_URLS.country),
      ]);
      setCenterTypes(Array.isArray(programsRes.data) ? programsRes.data : []);
      setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : []);
    } catch {
      toast.error(t("demo.errorLoadingData"));
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => { if (open) fetchOptions(); }, [open, fetchOptions]);

  // ── Fetch Booked Slots ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!selDay) return;
    const dateStr = `${calDate.year}-${String(calDate.month + 1).padStart(2, "0")}-${String(selDay).padStart(2, "0")}`;
    let cancelled = false;
    setLoadingSlots(true); setBookedSlots([]); setSelTime(null);
    axios.get(API_URLS.demoBookedSlots(dateStr))
      .then(res => { if (!cancelled) setBookedSlots(res.data.booked || []); })
      .catch(() => { if (!cancelled) setBookedSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [selDay, calDate]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const newErrors = {};
    if (!form.fullName.trim())                            newErrors.fullName    = t("demo.errorNameRequired");
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = t("demo.errorValidEmail");
    if (!form.center_name.trim())                         newErrors.center_name = t("demo.errorNameRequired");
    if (!form.size || isNaN(form.size) || +form.size < 1) newErrors.size       = t("demo.errorValidNumber");
    if (!form.country)                                    newErrors.country     = t("demo.errorSelectCountry");
    if (!form.type)                                       newErrors.type        = t("demo.errorSelectType");
    if (!form.info.trim())                                newErrors.info        = t("demo.errorInfoRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => { if (validateStep1()) setStep(2); };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!selDay || !selTime) return;
    const formattedDate = `${calDate.year}-${String(calDate.month + 1).padStart(2, "0")}-${String(selDay).padStart(2, "0")}`;
    const payload = {
      full_name: form.fullName, email: form.email,
      center_name: form.center_name, center_size: parseInt(form.size),
      country: form.country, center_type_id: parseInt(form.type),
      extra_info: form.info, demo_date: formattedDate, demo_time: selTime,
    };
    setIsLoading(true);
    try {
      const response = await axios.post(API_URLS.bookDemo(), payload);
      if (response.status >= 200 && response.status < 300) setSubmitted(true);
    } catch (err) {
      const result = err.response?.data;
      const serverError = result?.demo_time?.[0] || result?.non_field_errors?.[0];
      if (result?.demo_time) {
        const dateStr = `${calDate.year}-${String(calDate.month + 1).padStart(2, "0")}-${String(selDay).padStart(2, "0")}`;
        axios.get(API_URLS.demoBookedSlots(dateStr))
          .then(res => setBookedSlots(res.data.booked || []))
          .catch(() => {});
        setSelTime(null);
      }
      setErrors(prev => ({ ...prev, server: serverError || t("demo.errorServerFailed") }));
    } finally {
      setIsLoading(false);
    }
  }, [calDate, selDay, selTime, form, t]);

  // ── Calendar Helpers ───────────────────────────────────────────────────────
  const minDate    = getMinDate();
  const cells      = buildCalendarCells(calDate.year, calDate.month);
  const selDateObj = selDay ? new Date(calDate.year, calDate.month, selDay) : null;
  const selDateStr = selDateObj
    ? `${t(`days.${DAY_KEYS[selDateObj.getDay()]}`)}, ${t("months", { returnObjects: true })[calDate.month]} ${selDay}, ${calDate.year}`
    : "";

  function isDayAvail(d) {
    if (!d) return false;
    return new Date(calDate.year, calDate.month, d) >= minDate;
  }

  function prevMonth() {
    setCalDate(prev => ({
      year:  prev.month === 0 ? prev.year - 1 : prev.year,
      month: prev.month === 0 ? 11 : prev.month - 1,
    }));
    setSelDay(null); setSelTime(null); setBookedSlots([]);
  }

  function nextMonth() {
    setCalDate(prev => ({
      year:  prev.month === 11 ? prev.year + 1 : prev.year,
      month: prev.month === 11 ? 0 : prev.month + 1,
    }));
    setSelDay(null); setSelTime(null); setBookedSlots([]);
  }

  // ── Step Dots ──────────────────────────────────────────────────────────────
  const StepDots = () => (
    <Box
      sx={{
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", mb: 3, pb: 2.5,
      }}
    >
      {[1, 2].map(n => (
        <React.Fragment key={n}>
          <Box
            sx={{
              width: 28, height: 28, borderRadius: "50%", zIndex: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: n <= step ? "var(--s-purple-color)" : "var(--s-gray-color)",
              border: `2.5px solid ${n <= step ? "var(--s-purple-color)" : "var(--s-gray-color)"}`,
            }}
          >
            {n < step && (
              <Typography sx={{ color: "var(--contrast-color)", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                ✓
              </Typography>
            )}
          </Box>
          {n < 2 && (
            <Box
              sx={{
                flex: 1, height: 2, mx: 0.5,
                background: step >= 2 ? "var(--s-purple-color)" : "var(--s-gray-color)",
              }}
            />
          )}
        </React.Fragment>
      ))}
      <Typography
        sx={{
          position: "absolute", bottom: 0, fontSize: 10,
          fontWeight: 700, color: "text.secondary",
          [isAr ? "right" : "left"]: 0,
        }}
      >
        {t("demo.step", { n: 1 }) || "STEP 1"}
      </Typography>
      <Typography
        sx={{
          position: "absolute", bottom: 0, fontSize: 10,
          fontWeight: 700, color: "text.secondary",
          [isAr ? "left" : "right"]: 0,
        }}
      >
        {t("demo.step", { n: 2 }) || "STEP 2"}
      </Typography>
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex", maxWidth: 900, width: "95%",
          minHeight: 520, borderRadius: 4, overflow: "hidden",
          bgcolor: "background.paper", boxShadow: 24,
          direction: isAr ? "rtl" : "ltr",
        }}
      >
        {/* ── Left Panel ── */}
        <Box
          sx={{
            width: 280, flexShrink: 0, p: "40px 28px",
            background: "linear-gradient(160deg, var(--s-purple-dark-color) 0%, var(--s-purple-color) 100%)",
            color: "var(--contrast-color)",
            display: { xs: "none", sm: "flex" },
            flexDirection: "column", gap: 1.5,
          }}
        >
          <Box
            sx={{
              background: "rgba(255,255,255,0.2)", borderRadius: 5,
              px: 1.5, py: 0.5, width: "fit-content",
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
              {t("demo.getADemo") || "GET A DEMO"}
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={800}>
            {t("demo.leftTitle") || "Select a time for us to connect"}
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
            {t("demo.leftSub") || "Fill in your details and choose a date and time that's convenient for you!"}
          </Typography>
        </Box>

        {/* ── Right Panel ── */}
        <Box sx={{ flex: 1, p: "32px 28px", overflowY: "auto", maxHeight: "90vh", position: "relative" }}>

          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: "absolute", top: 12, [isAr ? "left" : "right"]: 12 }}
          >
            <BiX />
          </IconButton>

          <StepDots />

          {/* ── Success Screen ── */}
          {submitted ? (
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1} py={4}>
              <Typography sx={{ fontSize: 48 }}>🎉</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: "var(--s-purple-color)" }}>
                {t("demo.successTitle") || "You're booked!"}
              </Typography>
              <Typography color="text.secondary" fontSize={14}>
                {t("demo.successMsg")} <strong>{selDateStr}</strong> {t("demo.at")} <strong>{selTime}</strong>.
                <Box component="span" sx={{ fontSize: 13, mt: 1, display: "inline-block" }}>
                  {t("demo.emailSentNote")}
                </Box>
              </Typography>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  mt: 1, borderRadius: "10px", fontWeight: 700,
                  background: "linear-gradient(135deg, var(--s-purple-dark-color), var(--s-purple-color))",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {t("demo.done") || "Done"}
              </Button>
            </Box>

          /* ── Step 1: Info Form ── */
          ) : step === 1 ? (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t("demo.yourInfo") || "Your Information"}
              </Typography>

              {/* Full Name */}
              <Box mb={2}>
                <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                  {t("demo.fullName") || "Name"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                </Typography>
                <input
                  type="text" value={form.fullName}
                  className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
                  onChange={e => { setForm({ ...form, fullName: e.target.value }); setErrors(prev => ({ ...prev, fullName: undefined })); }}
                  placeholder={t("demo.fullNamePlaceholder") || "Your full name"}
                  disabled={isLoading}
                />
                {errors.fullName && <Typography className="errorText">{errors.fullName}</Typography>}
              </Box>

              {/* Email + Center Name */}
              <Box display="flex" gap={2} mb={2}>
                <Box flex={1}>
                  <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                    {t("demo.email") || "Email"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                  </Typography>
                  <input
                    type="email" value={form.email}
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    onChange={e => { setForm({ ...form, email: e.target.value }); setErrors(prev => ({ ...prev, email: undefined })); }}
                    placeholder="your@email.com" disabled={isLoading}
                  />
                  {errors.email && <Typography className="errorText">{errors.email}</Typography>}
                </Box>
                <Box flex={1}>
                  <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                    {t("demo.centerName") || "Center Name"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                  </Typography>
                  <input
                    type="text" value={form.center_name}
                    className={`form-control ${errors.center_name ? "is-invalid" : ""}`}
                    onChange={e => { setForm({ ...form, center_name: e.target.value }); setErrors(prev => ({ ...prev, center_name: undefined })); }}
                    placeholder={t("demo.centerNamePlaceholder") || "Center name"} disabled={isLoading}
                  />
                  {errors.center_name && <Typography className="errorText">{errors.center_name}</Typography>}
                </Box>
              </Box>

              {/* Center Size + Country */}
              <Box display="flex" gap={2} mb={2}>
                <Box flex={1}>
                  <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                    {t("demo.centerSize") || "Center Size"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                  </Typography>
                  <input
                    type="number" value={form.size}
                    className={`form-control ${errors.size ? "is-invalid" : ""}`}
                    onChange={e => { setForm({ ...form, size: e.target.value }); setErrors(prev => ({ ...prev, size: undefined })); }}
                    placeholder="e.g. 120" disabled={isLoading}
                  />
                  {errors.size && <Typography className="errorText">{errors.size}</Typography>}
                </Box>
                <Box flex={1}>
                  <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                    {t("demo.country") || "Country"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                  </Typography>
                  <select
                    value={form.country}
                    className={`form-control ${errors.country ? "is-invalid" : ""}`}
                    onChange={e => { setForm({ ...form, country: e.target.value }); setErrors(prev => ({ ...prev, country: undefined })); }}
                    disabled={isLoading || loadingData}
                  >
                    <option value="">{loadingData ? (t("loading") || "Loading...") : (t("demo.selectCountry") || "Select country")}</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{t(`countries.${c.code}`) || c.name}</option>)}
                  </select>
                  {errors.country && <Typography className="errorText">{errors.country}</Typography>}
                </Box>
              </Box>

              {/* Center Type */}
              <Box mb={2}>
                <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                  {t("demo.centerType") || "Center Type"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                </Typography>
                <select
                  value={form.type}
                  className={`form-control ${errors.type ? "is-invalid" : ""}`}
                  onChange={e => { setForm({ ...form, type: e.target.value }); setErrors(prev => ({ ...prev, type: undefined })); }}
                  disabled={isLoading || loadingData}
                >
                  <option value="">{loadingData ? (t("loading") || "Loading...") : (t("demo.selectType") || "Select center type")}</option>
                  {centerTypes.map(ct => (
                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                  ))}
                </select>
                {errors.type && <Typography className="errorText">{errors.type}</Typography>}
              </Box>

              {/* Additional Info */}
              <Box mb={2}>
                <Typography variant="body2" component="label" color="text.secondary" fontWeight={700} gutterBottom display="block">
                  {t("demo.additionalInfo") || "Additional Info"} <Box component="span" sx={{ color: "error.main" }}>*</Box>
                </Typography>
                <textarea
                  rows={3} value={form.info}
                  className={`form-control ${errors.info ? "is-invalid" : ""}`}
                  onChange={e => { setForm({ ...form, info: e.target.value }); setErrors(prev => ({ ...prev, info: undefined })); }}
                  disabled={isLoading} style={{ resize: "none" }}
                />
                {errors.info && <Typography className="errorText">{errors.info}</Typography>}
              </Box>

              <Box display="flex" justifyContent={isAr ? "flex-start" : "flex-end"}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    borderRadius: "10px", fontWeight: 700,
                    background: "linear-gradient(135deg, var(--s-purple-dark-color), var(--s-purple-color))",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  {t("demo.nextSteps") || "Next Steps"} {isAr ? "←" : "→"}
                </Button>
              </Box>
            </Box>

          /* ── Step 2: Calendar & Time ── */
          ) : (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t("demo.bookOnlineDemo") || "Book an Online Demo"}
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">

                {/* Calendar */}
                <Box
                  flex="1 1 240px"
                  sx={{ border: "1.5px solid var(--s-gray-color)", borderRadius: 3, p: "16px 14px" }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <IconButton
                      size="small" onClick={prevMonth}
                      sx={{ border: "1px solid var(--s-gray-color)", borderRadius: 1 }}
                    >
                      <BiChevronLeft />
                    </IconButton>
                    <Typography fontWeight={700} fontSize={14}>
                      {t("months", { returnObjects: true })[calDate.month]} {calDate.year}
                    </Typography>
                    <IconButton
                      size="small" onClick={nextMonth}
                      sx={{ border: "1px solid var(--s-gray-color)", borderRadius: 1 }}
                    >
                      <BiChevronRight />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", textAlign: "center" }}>
                    {DAY_KEYS.map(d => (
                      <Typography key={d} sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", py: 0.5 }}>
                        {t(`days.${d}`)}
                      </Typography>
                    ))}
                    {cells.map((d, i) => {
                      const avail      = isDayAvail(d);
                      const isSelected = d && selDay === d;
                      return (
                        <Box
                          key={i}
                          onClick={() => { if (avail) { setSelDay(d); setSelTime(null); } }}
                          sx={{
                            height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                            borderRadius: 2, fontSize: 13, cursor: avail ? "pointer" : "default",
                            background: isSelected
                              ? "var(--s-purple-color)"
                              : avail
                              ? "color-mix(in srgb, var(--s-purple-color) 10%, white)"
                              : "transparent",
                            color: isSelected
                              ? "var(--contrast-color)"
                              : avail
                              ? "var(--s-purple-color)"
                              : "var(--bs-secondary-color)",
                            "&:hover": avail && !isSelected
                              ? { background: "color-mix(in srgb, var(--s-purple-color) 20%, white)" }
                              : {},
                          }}
                        >
                          {d}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Time Slots */}
                {selDay && (
                  <Box
                    sx={{ flex: "0 0 160px", border: "1.5px solid var(--s-gray-color)", borderRadius: 3, p: "16px 14px" }}
                  >
                    <Typography fontWeight={700} fontSize={14} mb={0.5}>
                      {t(`days.${DAY_KEYS[selDateObj.getDay()]}`)}, {t("months", { returnObjects: true })[calDate.month]} {selDay}
                    </Typography>
                    <Typography fontSize={12} color="text.secondary" mb={1}>
                      {t("demo.duration") || "Duration: 30 min"}
                    </Typography>

                    {loadingSlots ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {TIMES.slice(0, 8).map(t => (
                          <Box
                            key={t}
                            sx={{
                              height: 36, borderRadius: 2,
                              bgcolor: "var(--s-gray-color)",
                              animation: "pulse 1.4s ease-in-out infinite",
                              "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 260, overflowY: "auto" }}>
                        {TIMES.map(time => {
                          const isBooked   = bookedSlots.includes(time);
                          const isSelected = selTime === time;
                          return (
                            <Button
                              key={time}
                              disabled={isBooked}
                              title={isBooked ? (t("demo.slotTaken") || "Already booked") : ""}
                              onClick={() => !isBooked && setSelTime(time)}
                              sx={{
                                borderRadius: "8px", fontWeight: 600, fontSize: 13,
                                width: "100%", py: "8px", minWidth: 0, position: "relative",
                                border: `1.5px solid ${isBooked ? "var(--s-gray-color)" : "var(--s-purple-color)"}`,
                                background: isSelected
                                  ? "var(--s-purple-color)"
                                  : isBooked
                                  ? "var(--s-gray-color)"
                                  : "var(--surface-color)",
                                color: isSelected
                                  ? "var(--contrast-color)"
                                  : isBooked
                                  ? "var(--bs-secondary-color)"
                                  : "var(--s-purple-color)",
                                cursor: isBooked ? "not-allowed" : "pointer",
                                "&:hover": !isBooked && !isSelected
                                  ? { background: "color-mix(in srgb, var(--s-purple-color) 10%, white)" }
                                  : {},
                                "&.Mui-disabled": {
                                  color: "var(--bs-secondary-color)",
                                  background: "var(--s-gray-color)",
                                  border: "1.5px solid var(--s-gray-color)",
                                },
                              }}
                            >
                              {time}
                              {isBooked && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 9, position: "absolute",
                                    [isAr ? "left" : "right"]: 7,
                                    top: "50%", transform: "translateY(-50%)",
                                    color: "var(--bs-secondary-color)",
                                  }}
                                >
                                  ✕
                                </Box>
                              )}
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {errors.server && (
                <Typography className="errorText" mt={1}>{errors.server}</Typography>
              )}

              <Box display="flex" justifyContent="space-between" mt={2}>
                {/* Back Button */}
                <Button
                  variant="outlined"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                  sx={{
                    borderRadius: "10px", fontWeight: 600,
                    color: "text.secondary",
                    border: "1.5px solid var(--s-gray-color)",
                    background: "var(--surface-color)",
                    "&:hover": { background: "var(--s-gray-color)", border: "1.5px solid var(--s-gray-color)" },
                  }}
                >
                  {isAr ? "→" : "←"} {t("demo.back") || "Back"}
                </Button>

                {/* Confirm Button */}
                <Button
                  variant="contained"
                  disabled={!selDay || !selTime || isLoading}
                  onClick={handleSubmit}
                  startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{
                    borderRadius: "10px", fontWeight: 700,
                    background: "linear-gradient(135deg, var(--s-purple-dark-color), var(--s-purple-color))",
                    opacity: selDay && selTime && !isLoading ? 1 : 0.5,
                    "&:hover": { opacity: 0.9 },
                    "&.Mui-disabled": {
                      background: "linear-gradient(135deg, var(--s-purple-dark-color), var(--s-purple-color))",
                      color: "var(--contrast-color)",
                    },
                  }}
                >
                  {isLoading ? (t("demo.confirming") || "Confirming...") : (t("demo.confirm") || "Confirm")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}